// MfaSetupPage — η.2 of the MFA program (CHG-1512 / ITER-3629).
//
// Route: /mfa-setup. Unauth-accessible (the setup_token IS the auth
// carrier — same pattern as ResetPasswordPage). Routed from LoginPage
// when AuthContext.login() returns { kind: "mfa_setup_required" }.
// Backend issues a setup_token when the account hits a "MFA required
// but not enrolled" policy gate (platform-admin role, org-policy, or
// user-policy).
//
// Flow:
//   1. On mount: POST /api/v1/auth/mfa-setup/enroll { setup_token }
//      → MfaEnrollResponse { secret, otpauth_uri, qr_code_base64 }
//      Show QR code (rendered client-side via qrcode.react from
//      otpauth_uri) + the secret as a copyable fallback for users
//      whose authenticator doesn't scan.
//   2. User scans / pastes into authenticator → types 6-digit TOTP
//      code into the input below.
//   3. Submit: POST /api/v1/auth/mfa-setup/verify { setup_token, code }
//      → LoginResponse { access_token, account, initial_recovery_codes }
//      Verify activates the enrollment AND issues a session token in
//      the same round-trip.
//   4. Show recovery codes ONCE (the BE returns them exactly once per
//      verify — the column is hashed in the DB and cannot be retrieved
//      again). User clicks "I've saved these" → continue.
//   5. AuthContext.completeMfaLogin() writes the token + hydrates the
//      account → navigate to returnTo (passed via location.state from
//      LoginPage; defaults to "/").
//
// Per mfa-program.md doctrine: "use canonical OtpField if exists, else
// minimal numeric input (6 digits, autocomplete='one-time-code')". No
// canonical OtpField in v2 yet — minimal input with inputmode="numeric"
// + autocomplete="one-time-code" + maxLength=6. A DRIFT for the
// canonical primitive extraction can land later if other MFA surfaces
// (sibling η.1 MfaChallengePage + post-auth /me/mfa enrolment from
// out-of-scope work) repeat the pattern.

import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "../../contexts/AuthContext";
import { apiClient } from "../../api/client";
import { AuthLayout } from "./AuthLayout";
import { inputStyle, labelStyle, errorStyle, submitStyle } from "./styles";

type State =
  | { phase: "enrolling" } // POST /enroll in flight
  | { phase: "enroll_error"; message: string }
  | { phase: "form"; secret: string; otpauthUri: string; qrCodeBase64: string }
  | { phase: "submitting"; secret: string; otpauthUri: string; qrCodeBase64: string }
  | { phase: "verify_error"; secret: string; otpauthUri: string; qrCodeBase64: string; message: string }
  | { phase: "recovery_codes"; codes: string[]; accessToken: string; account: AccountSeed };

interface AccountSeed {
  id: string;
  email: string;
  full_name: string | null;
}

interface EnrollResponse {
  secret: string;
  otpauth_uri: string;
  qr_code_base64: string;
}

interface VerifyResponse {
  access_token: string | null;
  refresh_token: string | null;
  account: AccountSeed | null;
  initial_recovery_codes: string[] | null;
}

interface LocationState {
  setup_token?: string;
  reason_code?: string;
  returnTo?: string;
}

function safeReturnTo(raw: string | undefined | null): string {
  // Same open-redirect guard as LoginPage.safeReturnTo — confine
  // navigation to in-app paths.
  if (!raw) return "/";
  if (!raw.startsWith("/") || raw.startsWith("//")) return "/";
  return raw;
}

// Chunk the TOTP secret into 4-char groups for legibility (the standard
// presentation in 1Password / Authy / Google Authenticator manual-entry
// UIs). Pure presentational; the underlying secret is exactly what the
// BE stores.
function chunkSecret(secret: string): string {
  return secret.replace(/(.{4})/g, "$1 ").trim();
}

function reasonCopy(reason: string): string {
  switch (reason) {
    case "platform-admin":
      return "Platform admins must enroll in MFA to sign in. Add the code below to your authenticator app, then enter the 6-digit code to finish.";
    case "org-policy":
      return "Your organization requires multi-factor authentication. Add the code below to your authenticator app, then enter the 6-digit code to finish.";
    case "user-policy":
      return "Multi-factor authentication is required on your account. Add the code below to your authenticator app, then enter the 6-digit code to finish.";
    default:
      return "Set up multi-factor authentication to continue. Scan the QR code with your authenticator app, then enter the 6-digit code to finish.";
  }
}

async function copyToClipboard(text: string): Promise<boolean> {
  // Best-effort: navigator.clipboard isn't available in some embedded
  // browsers / older Safari. Falls back to a document.execCommand
  // textarea trick. Returns boolean rather than throwing so the UI can
  // show a "Copy failed" hint without crashing.
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy path
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export default function MfaSetupPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { completeMfaLogin } = useAuth();

  const locState = (location.state ?? {}) as LocationState;
  const setupToken = locState.setup_token ?? "";
  const reasonCode = locState.reason_code ?? "";
  const returnTo = safeReturnTo(locState.returnTo);

  const [state, setState] = useState<State>({ phase: "enrolling" });
  const [code, setCode] = useState("");
  const [copyHint, setCopyHint] = useState<"secret" | "uri" | "codes" | null>(null);

  // No setup_token → user landed here directly (bookmarked link,
  // back-button after a successful login, etc.). Bounce to /login so
  // the LoginPage can re-issue a fresh setup_token through the normal
  // password-login → mfa_setup_required path.
  useEffect(() => {
    if (!setupToken) {
      navigate("/login", { replace: true });
    }
  }, [setupToken, navigate]);

  // Enroll on mount. setup_token is the auth carrier; the BE validates
  // its JWT signature + purpose=mfa-setup claim. 401/403 → token is
  // stale; bounce to /login. Other errors → render an inline retry.
  useEffect(() => {
    if (!setupToken) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await apiClient().post<EnrollResponse>("/auth/mfa-setup/enroll", {
          setup_token: setupToken,
        });
        if (cancelled) return;
        setState({
          phase: "form",
          secret: r.data.secret,
          otpauthUri: r.data.otpauth_uri,
          qrCodeBase64: r.data.qr_code_base64,
        });
      } catch (e: unknown) {
        if (cancelled) return;
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) {
          navigate("/login", { replace: true });
          return;
        }
        const detail =
          (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
        setState({
          phase: "enroll_error",
          message: detail ?? "Couldn't start MFA setup. Please go back and sign in again.",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setupToken, navigate]);

  const onSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (state.phase !== "form" && state.phase !== "verify_error") return;
      if (code.length !== 6 || !/^\d{6}$/.test(code)) {
        setState({
          ...state,
          phase: "verify_error",
          message: "Enter the 6-digit code from your authenticator app.",
        });
        return;
      }
      const baseline = state;
      setState({
        phase: "submitting",
        secret: baseline.secret,
        otpauthUri: baseline.otpauthUri,
        qrCodeBase64: baseline.qrCodeBase64,
      });
      try {
        const r = await apiClient().post<VerifyResponse>("/auth/mfa-setup/verify", {
          setup_token: setupToken,
          code,
        });
        if (!r.data.access_token || !r.data.account) {
          throw new Error("Verify response missing access_token or account.");
        }
        // Recovery codes are returned ONCE — show them before navigating
        // off so the user has a chance to save them. If the BE somehow
        // returned an empty list (shouldn't happen for the initial
        // enrollment), skip straight to completion.
        const codes = Array.isArray(r.data.initial_recovery_codes)
          ? r.data.initial_recovery_codes
          : [];
        if (codes.length === 0) {
          await completeMfaLogin(r.data.access_token, r.data.account);
          navigate(returnTo, { replace: true });
          return;
        }
        setState({
          phase: "recovery_codes",
          codes,
          accessToken: r.data.access_token,
          account: r.data.account,
        });
      } catch (e: unknown) {
        // CHG-2954 (UM program Slice G) — 401 on verify means the
        // setup_token itself is invalid/expired (validate_mfa_setup_token;
        // a wrong TOTP code is 422, no-pending-enrollment is 404), so
        // retyping the code can never succeed. Bounce to /login to mint a
        // fresh setup_token — same handling the enroll-on-mount path
        // already applies.
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 401) {
          navigate("/login", { replace: true });
          return;
        }
        const detail =
          (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
        setState({
          phase: "verify_error",
          secret: baseline.secret,
          otpauthUri: baseline.otpauthUri,
          qrCodeBase64: baseline.qrCodeBase64,
          message: detail ?? "That code didn't match. Try again with a fresh code from your app.",
        });
      }
    },
    [state, code, setupToken, completeMfaLogin, navigate, returnTo],
  );

  const continueAfterRecovery = useCallback(async () => {
    if (state.phase !== "recovery_codes") return;
    await completeMfaLogin(state.accessToken, state.account);
    navigate(returnTo, { replace: true });
  }, [state, completeMfaLogin, navigate, returnTo]);

  const chunkedSecret = useMemo(() => {
    if (state.phase === "form" || state.phase === "submitting" || state.phase === "verify_error") {
      return chunkSecret(state.secret);
    }
    return "";
  }, [state]);

  // Recovery-codes phase: distinct layout that takes over the form area.
  if (state.phase === "recovery_codes") {
    return (
      <AuthLayout pageId="mfa-setup" title="Save your recovery codes" subtitle="Store these somewhere safe — they're shown only once.">
        <p style={{ fontSize: 13, color: "var(--color-text-muted)", marginBottom: 12 }}>
          Each code can be used once if you ever lose access to your authenticator app. Treat them like passwords.
        </p>
        <ul
          data-testid="recovery-codes-list"
          style={{
            listStyle: "none",
            margin: "0 0 16px",
            padding: 12,
            background: "var(--color-bg-subtle)",
            borderRadius: 6,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            fontFamily: "monospace",
            fontSize: 13,
          }}
        >
          {state.codes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <button
          type="button"
          onClick={async () => {
            const ok = await copyToClipboard(state.codes.join("\n"));
            if (ok) setCopyHint("codes");
          }}
          style={{ ...submitStyle(false), background: "transparent", color: "var(--color-text)", border: "1px solid var(--color-border)" }}
          data-testid="copy-recovery-codes"
        >
          {copyHint === "codes" ? "Copied" : "Copy all to clipboard"}
        </button>
        <button
          type="button"
          onClick={continueAfterRecovery}
          style={{ ...submitStyle(false), marginTop: 12 }}
          data-testid="continue-after-recovery"
        >
          I&rsquo;ve saved them — continue
        </button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout pageId="mfa-setup" title="Set up two-factor authentication" subtitle={reasonCopy(reasonCode)}>
      {state.phase === "enrolling" ? (
        <p data-testid="mfa-setup-loading" style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
          Loading setup details&hellip;
        </p>
      ) : null}

      {state.phase === "enroll_error" ? (
        <div role="alert" style={errorStyle} data-testid="mfa-setup-enroll-error">
          {state.message}
        </div>
      ) : null}

      {(state.phase === "form" || state.phase === "submitting" || state.phase === "verify_error") ? (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              padding: 16,
              background: "white",
              borderRadius: 6,
              marginBottom: 16,
            }}
          >
            <QRCodeSVG
              value={state.otpauthUri}
              size={176}
              level="M"
              data-testid="mfa-setup-qr"
            />
            <p style={{ fontSize: 12, color: "var(--color-text-muted)", margin: 0 }}>
              Scan with Google Authenticator, 1Password, Authy, etc.
            </p>
          </div>

          <details style={{ marginBottom: 16 }}>
            <summary style={{ cursor: "pointer", fontSize: 13, color: "var(--color-text-muted)" }}>
              Can&rsquo;t scan? Enter the secret manually
            </summary>
            <div style={{ marginTop: 8, padding: 12, background: "var(--color-bg-subtle)", borderRadius: 6 }}>
              <code
                data-testid="mfa-setup-secret"
                style={{ display: "block", fontFamily: "monospace", fontSize: 13, wordBreak: "break-all" }}
              >
                {chunkedSecret}
              </code>
              <button
                type="button"
                onClick={async () => {
                  const ok = await copyToClipboard(state.secret);
                  if (ok) setCopyHint("secret");
                }}
                style={{
                  marginTop: 8,
                  padding: "4px 10px",
                  fontSize: 12,
                  background: "transparent",
                  border: "1px solid var(--color-border)",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
                data-testid="copy-secret"
              >
                {copyHint === "secret" ? "Copied" : "Copy secret"}
              </button>
            </div>
          </details>

          <form onSubmit={onSubmit} data-form="mfa-setup">
            <label style={labelStyle}>
              6-digit code from your app
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                pattern="\d{6}"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required
                autoFocus
                style={inputStyle}
                data-testid="mfa-setup-code-input"
              />
            </label>

            {state.phase === "verify_error" ? (
              <div role="alert" style={errorStyle} data-testid="mfa-setup-verify-error">
                {state.message}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={state.phase === "submitting" || code.length !== 6}
              style={submitStyle(state.phase === "submitting")}
              data-testid="mfa-setup-submit"
            >
              {state.phase === "submitting" ? "Verifying…" : "Verify & continue"}
            </button>
          </form>
        </>
      ) : null}
    </AuthLayout>
  );
}
